/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';

import { GET_REPORT_QUERY } from './seller/SellerReportTable';
import type { ModalDialog } from '../../../type';

const DATE_FORMAT = 'yyyy-MM-dd';

const DELETE_INVOICE_MUTATION = gql`
    mutation deleteInvoice($input: DeleteInvoiceInput!) {
        deleteInvoice(input: $input) {
            invoiceId
        }
    }
`;

type RemoveInvoiceDialogProps = {
    invoiceId: string,
} & ModalDialog;

const ModalContent = styled(Modal.Content)`
    text-align: center;
`;

function RemoveInvoiceDialog({
    trigger,
    open,
    onClose,
    invoiceId,
}: RemoveInvoiceDialogProps) {
    const reportFilter = useSelector(({ report }) => report.get('reportFilter'));
    const user = useSelector(({ user }) => user.get('id'));
    const salon = useSelector(({ user }) => user.get('salon'));
    const fromDate = useSelector(({ report }) =>
        format(new Date(report.get('fromDate')), DATE_FORMAT),
    );
    const toDate = useSelector(({ report }) =>
        format(new Date(report.get('toDate')), DATE_FORMAT),
    );
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const [deleteInvoice] = useMutation(DELETE_INVOICE_MUTATION);

    const onDelete = () => {
        const mutation = deleteInvoice({
            variables: {
                input: {
                    userId: user,
                    invoiceId,
                },
            },
            optimisticResponse: {
                __typename: 'Mutation',
                deleteInvoice: {
                    __typename: 'DeleteInvoicePayload',
                    invoiceId,
                },
            },
            update: (store, { data: { deleteInvoice: deletedInvoice } }) => {
                try {
                    const data = store.readQuery({
                        query: GET_REPORT_QUERY,
                        variables: {
                            salon,
                            fromDate,
                            toDate,
                            deleted: true,
                            showArchive,
                            reportFilter,
                            lang: currentLanguage,
                        },
                    });

                    const filterFn = invoice =>
                        invoice.invoiceId !== deletedInvoice.invoiceId;

                    const adminReportInvoices = data.viewer.salon.administrator.report.invoices.filter(
                        filterFn,
                    );

                    const updatedAdministrators = data.viewer.salon.administrators.map(
                        administrator => ({
                            ...administrator,
                            report: {
                                ...administrator.report,
                                invoices: administrator.report.invoices.filter(
                                    filterFn,
                                ),
                            },
                        }),
                    );

                    const updatedMasters = data.viewer.salon.masters.nodes.map(
                        master => ({
                            ...master,
                            report: {
                                ...master.report,
                                invoices: master.report.invoices.filter(
                                    filterFn,
                                ),
                            },
                        }),
                    );

                    const updatedData = {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                administrator: {
                                    ...data.viewer.salon.administrator,
                                    report: {
                                        ...data.viewer.salon.administrator
                                            .report,
                                        invoices: adminReportInvoices,
                                    },
                                },
                                administrators: updatedAdministrators,
                                masters: {
                                    ...data.viewer.salon.masters,
                                    nodes: updatedMasters,
                                },
                            },
                        },
                    };

                    store.writeQuery({
                        query: GET_REPORT_QUERY,
                        variables: {
                            salon,
                            fromDate,
                            toDate,
                            deleted: true,
                            showArchive,
                            reportFilter,
                            lang: currentLanguage,
                        },
                        data: updatedData,
                    });
                } catch (e) {
                    // It's safe to ignore this exception
                }
            },
        });

        return mutation
            .then(() => {
                // Close modal dialog after invoice was deleted
                onClose();
            })
            .catch(() => {
                // TODO: Do something with an error when invoice was not deleted
            });
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.report.removeInvoice.title"
                    defaultMessage="Remove Invoice"
                />
            </Header>
            <ModalContent>
                <FormattedMessage
                    id="pages.report.removeInvoice.message"
                    defaultMessage="Are you sure you want to remove this invoice?"
                    tagName="p"
                />
            </ModalContent>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.report.removeInvoice.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.report.removeInvoice.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default RemoveInvoiceDialog;
